import { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Checkbox,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Styled components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
  '& .MuiTableHead-root': {
    backgroundColor: '#E3F2FD',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    height: 50,
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiTableCell-head': {
    fontWeight: 600,
    color: theme.palette.text.primary,
    borderBottom: `2px solid ${theme.palette.divider}`,
    backgroundColor: '#E3F2FD',
  },
  '& .MuiTableCell-body': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)': {
    backgroundColor: '#F8FAFC',
  },
  '& .MuiTableRow-root:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme, selected }) => ({
  backgroundColor: selected
    ? theme.palette.primary.light + '20'
    : 'transparent',
  '&:hover': {
    backgroundColor: selected
      ? theme.palette.primary.light + '30'
      : theme.palette.action.hover,
  },
}));

const EmptyState = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

// Utility function to get nested object values
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

const DataTable = ({
  columns = [],
  data = [],
  title,
  onRowClick,
  onEdit,
  onDelete,
  onView,
  selectable = false,
  onSelectionChange,
  sortable = true,
  pagination = true,
  rowsPerPageOptions = [5, 10, 25, 50],
  defaultRowsPerPage = 10,
  loading = false,
  emptyMessage = 'No data available',
  showActions = true,
  stickyHeader = false,
  maxHeight,
  sx,
}) => {
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [selected, setSelected] = useState([]);

  // Sorting function
  const handleRequestSort = property => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Selection functions
  const handleSelectAllClick = event => {
    if (event.target.checked) {
      const newSelected = data.map(n => n.id || n);
      setSelected(newSelected);
      onSelectionChange?.(newSelected);
      return;
    }
    setSelected([]);
    onSelectionChange?.([]);
  };

  const handleClick = id => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
    onSelectionChange?.(newSelected);
  };

  const isSelected = id => selected.indexOf(id) !== -1;

  // Pagination functions
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortable || !orderBy) return data;

    return [...data].sort((a, b) => {
      const aValue = getNestedValue(a, orderBy);
      const bValue = getNestedValue(b, orderBy);

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'string') {
        return order === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return order === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }, [data, order, orderBy, sortable]);

  // Paginate data
  const paginatedData = useMemo(() => {
    if (!pagination) return sortedData;
    return sortedData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [sortedData, page, rowsPerPage, pagination]);

  // Render cell content
  const renderCell = (row, column) => {
    const value = getNestedValue(row, column.field);

    if (column.render) {
      return column.render(value, row);
    }

    if (column.type === 'chip') {
      const chipColor =
        typeof column.chipColor === 'function'
          ? column.chipColor(value)
          : column.chipColor || 'default';

      return (
        <Chip
          label={String(value || '')}
          color={chipColor}
          size="small"
          variant={column.chipVariant || 'filled'}
        />
      );
    }

    if (column.type === 'date') {
      return new Date(value).toLocaleDateString();
    }

    if (column.type === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    // Ensure we always return a string for MUI components
    return String(value || '');
  };

  // Render actions
  const renderActions = row => (
    <Box sx={{ display: 'flex', gap: 1 }}>
      {onView && (
        <Tooltip title="View">
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              onView(row);
            }}
          >
            <ViewIcon />
          </IconButton>
        </Tooltip>
      )}
      {onEdit && (
        <Tooltip title="Edit">
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              onEdit(row);
            }}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
      )}
      {onDelete && (
        <Tooltip title="Delete">
          <IconButton
            size="small"
            color="error"
            onClick={e => {
              e.stopPropagation();
              onDelete(row);
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  if (loading) {
    return (
      <Paper sx={{ p: 2, textAlign: 'center' }}>
        <Typography>Loading...</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden', ...sx }}>
      {/* Table Header */}
      {(title || showActions) && (
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {title && (
              <Typography variant="h6" component="h2">
                {title}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Table */}
      <StyledTableContainer
        sx={{
          maxHeight: maxHeight || 'calc(100vh - 300px)',
          scrollbarWidth: 'thin',
        }}
      >
        <Table stickyHeader={stickyHeader} size="medium">
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selected.length > 0 && selected.length < data.length
                    }
                    checked={data.length > 0 && selected.length === data.length}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
              )}
              {columns.map(column => (
                <TableCell
                  key={column.field}
                  align={String(column.align || 'left')}
                  sortDirection={orderBy === column.field ? order : false}
                  sx={{ minWidth: column.minWidth, width: column.width }}
                >
                  {sortable && column.sortable !== false ? (
                    <TableSortLabel
                      active={orderBy === column.field}
                      direction={
                        orderBy === column.field ? String(order) : 'asc'
                      }
                      onClick={() => handleRequestSort(column.field)}
                    >
                      {String(column.header || '')}
                    </TableSortLabel>
                  ) : (
                    String(column.header || '')
                  )}
                </TableCell>
              ))}
              {showActions && (onView || onEdit || onDelete) && (
                <TableCell align="center" sx={{ width: '10%' }}>
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={
                    columns.length +
                    (selectable ? 1 : 0) +
                    (showActions ? 1 : 0)
                  }
                >
                  <EmptyState>
                    <Typography variant="body2">{emptyMessage}</Typography>
                  </EmptyState>
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, index) => {
                const isItemSelected = isSelected(row.id || row);
                const labelId = `table-checkbox-${index}`;

                return (
                  <StyledTableRow
                    hover
                    onClick={() => onRowClick?.(row)}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.id || index}
                    selected={isItemSelected}
                    sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                  >
                    {selectable && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          inputProps={{
                            'aria-labelledby': labelId,
                          }}
                          onClick={e => e.stopPropagation()}
                          onChange={() => handleClick(row.id || row)}
                        />
                      </TableCell>
                    )}
                    {columns.map(column => (
                      <TableCell
                        key={column.field}
                        align={String(column.align || 'left')}
                        sx={{ minWidth: column.minWidth, width: column.width }}
                      >
                        {renderCell(row, column)}
                      </TableCell>
                    ))}
                    {showActions && (onView || onEdit || onDelete) && (
                      <TableCell align="center">{renderActions(row)}</TableCell>
                    )}
                  </StyledTableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>

      {/* Pagination */}
      {pagination && data.length > 0 && (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </Paper>
  );
};

export default DataTable;
